import { runSavePipeline } from './saveWorkerCore';

/**
 * Main-thread client for saveWorker.
 *
 * Lazily creates a single module Worker on first call. Each request gets
 * a monotonic id so concurrent saves don't cross replies. The input
 * arrayBuffer is sliced before transfer — the original stays usable on
 * the main thread (the viewer keeps re-rendering from it; Sign holds it
 * in state for re-applies).
 *
 * Falls back to the synchronous saveAllChanges (via runSavePipeline) when:
 *   - typeof Worker === 'undefined' (SSR, old browsers, Node)
 *   - new Worker(...) throws (CSP block, missing module)
 *   - the worker posts back { error } for our id
 *   - worker.onerror fires before our id ever resolved
 *
 * The fallback resolves with the sync result. Worker failure is never
 * surfaced to the caller unless the sync path also throws.
 */

let worker = null;
let workerBroken = false;
let nextId = 1;
const inflight = new Map(); // id -> { resolve, reject, buildSyncPayload }

function ensureWorker() {
  if (workerBroken) return null;
  if (worker) return worker;
  if (typeof Worker === 'undefined') {
    workerBroken = true;
    return null;
  }
  try {
    worker = new Worker(new URL('./saveWorker.js', import.meta.url), { type: 'module' });
  } catch (_) {
    workerBroken = true;
    return null;
  }
  worker.onmessage = (ev) => {
    const { id, bytes, error, progress } = ev.data || {};
    const entry = inflight.get(id);
    if (!entry) return;
    if (typeof progress === 'number') { entry.onProgress?.(progress); return; }
    inflight.delete(id);
    if (error) {
      // Worker reported a per-call failure — fall through to sync. If sync
      // also throws, that's a real bug, propagate it.
      runSavePipeline(entry.buildSyncPayload()).then(entry.resolve, entry.reject);
      return;
    }
    entry.resolve(bytes);
  };
  worker.onerror = () => {
    // Catastrophic worker error — drain all in-flight requests through
    // the sync path and mark the worker dead so future calls skip it.
    workerBroken = true;
    const drained = Array.from(inflight.entries());
    inflight.clear();
    for (const [, entry] of drained) {
      runSavePipeline(entry.buildSyncPayload()).then(entry.resolve, entry.reject);
    }
    try { worker.terminate(); } catch (_) {}
    worker = null;
  };
  return worker;
}

/**
 * Save a PDF off the main thread.
 *
 * @param {{
 *   arrayBuffer: ArrayBuffer,
 *   edits?: Array,
 *   annotations?: Array,
 *   options?: { fieldData?: Array, flatten?: boolean, preserveOverlays?: boolean },
 * }} args
 * @returns {Promise<Uint8Array>}
 */
export async function saveViaWorker({ arrayBuffer, edits = [], annotations = [], options = {}, onProgress }) {
  // Closure that builds a fresh sync payload on demand. The fallback paths
  // can't reuse the transferred copy (transfer detaches it), so we always
  // re-slice from the caller's source buffer.
  const buildSyncPayload = () => ({
    arrayBuffer: arrayBuffer.slice(0),
    edits,
    annotations,
    options: options.contentRemoval && onProgress ? { ...options, contentRemoval: { ...options.contentRemoval, onProgress } } : options,
  });

  const w = ensureWorker();
  if (!w) {
    return runSavePipeline(buildSyncPayload());
  }

  // Defensive copy for transfer to worker. The original arrayBuffer stays
  // intact for re-renders / re-applies on the main thread.
  const transferCopy = arrayBuffer.slice(0);
  const transferPayload = { arrayBuffer: transferCopy, edits, annotations, options };

  return new Promise((resolve, reject) => {
    const id = nextId++;
    inflight.set(id, { resolve, reject, buildSyncPayload, onProgress });
    try {
      w.postMessage({ id, payload: transferPayload }, [transferCopy]);
    } catch (_) {
      // postMessage failed (e.g. unsupported transfer). Fall back to sync.
      inflight.delete(id);
      runSavePipeline(buildSyncPayload()).then(resolve, reject);
    }
  });
}
